<template>
  <div
    v-if="item.optionMatrixRecord && item.optionMatrixRecord.id"
    class="sm:flex product-info p-4"
  >
    <div style="min-width: 50%">
      <tml-image
        class="product-preview"
        :src="item.images ? item.images.size(640) : null"
        :alt="item.product.name"
        :src-set="[{width: 640, height: 640}]"
        :loading-ratio="1.053"
        lazy-load
        min-height="320px"
      />
    </div>
    <div style="width: 100%">
      <h4>
        {{ item.product.name }} - Size: {{ item.options.Size }}
        {{ item.options.Colour ? ` - Colour: ${item.options.Colour}` : '' }}
      </h4>
      <p
        v-if="
          !app.isAtlas &&
          order.status.code !== 'paid' &&
          item.product.isCustomProduct(true)
        "
      >
        Sorry, we are unable to accept returns for custom products.
      </p>
      <template v-else-if="!added && item.activeQuantity">
        <tml-form
          :ref="`setOrderItemReturnForm${item.id}`"
          :form-name="`set-order-item-return-form-${item.id}`"
        >
          <tml-new-form
            :ref="`setOrderItemReturnNewForm${item.id}`"
            :name="`set-order-item-return-new-form-${item.id}`"
            style="margin-bottom: 150px !important"
          >
            <tml-new-select
              v-model="returnType"
              class="mt-2 mb-4"
              name="returnType"
              label="What do you want to do?"
              :options="getReturnTypeOptions(item.product)"
              height="300px"
            />
            <template v-if="returnType.value !== 'keep'">
              <tml-new-select
                v-if="item.activeQuantity > 1"
                v-model="quantity"
                class="mt-2 mb-4"
                name="quantity"
                :label="`How many do you want to ${returnType.value}?`"
                :options="getQuantityOptions(item)"
                :validation-rules="{
                  required: true,
                }"
                height="300px"
              />
              <tml-new-select
                v-if="forceReason"
                :key="returnType.value"
                v-model="reason"
                class="mb-4"
                name="return-reason"
                :label="`Why do you want to ${returnType.value} these items?`"
                :options="returnReasonOptions"
                :validation-rules="{
                  required: true,
                }"
                height="250px"
              />
              <div v-if="returnType.value === 'exchange'" class="exchange-for">
                <tml-new-select
                  v-if="productSizeOptions.length"
                  v-model="size"
                  class="mb-4"
                  name="size"
                  label="Select a size"
                  :options="productSizeOptions"
                  @input="size = $event"
                />
                <tml-color-selector
                  class="mb-4"
                  field-name="color"
                  :colors="productColourOptions"
                  :pre-selected="item.options.Colour"
                  @input="colour = $event"
                />
              </div>
            </template>
            <tml-item-tray v-if="returnType.value !== 'keep'">
              <template #primary>
                <tml-button fill primary text="Add" @click="addItem" />
              </template>
              <template #secondary>
                <tml-button fill text="Cancel" @click="resetFields" />
              </template>
            </tml-item-tray>
          </tml-new-form>
        </tml-form>
      </template>
      <template v-else-if="!item.activeQuantity">
        <p>This item has already been refunded or exchanged.</p>
      </template>
      <template v-else>
        <div>
          <p v-if="returnType.value === 'exchange'">
            <font-awesome-icon :icon="faExchange" /> Exchange
            <b>{{ quantity.value }}</b> of these items for size
            <b>{{ size.value }}</b> in <b>{{ colour }}</b>.
          </p>
          <p v-else>
            <font-awesome-icon :icon="faUndoAlt" /> Refund
            <b>{{ quantity.value }}</b> of these items.
          </p>
          <tml-button
            text="Undo"
            class="cursor-pointer mt-4"
            @click="removeItem(item)"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {Order, OrderItem} from '@teemill/common/models';

import {faUndoAlt, faExchange} from '@fortawesome/pro-light-svg-icons';

export default {
  name: 'OrderItemReturn',

  inject: ['app'],

  props: {
    order: {
      type: Order,
      required: true,
    },

    item: {
      type: OrderItem,
      required: true,
    },

    returnReasons: {
      type: Array,
      required: true,
    },

    availableOptions: {
      required: true,
    },

    forceReason: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      added: false,

      quantity: {
        text: 1,
        value: 1,
      },

      size: this.item.options.Size,
      reason: null,
      colour: this.item.options.Colour,

      returnType: {
        text: 'Keep',
        value: 'keep',
      },

      faUndoAlt,
      faExchange,
    };
  },

  computed: {
    productColourOptions() {
      return Object.keys(this.availableOptions);
    },

    productSizeOptions() {
      const options = [];
      const formattedOptions = [];

      const option = this.availableOptions[this.colour];
      const sizes = option ? option.sizes : {};

      Object.keys(sizes).forEach(size => {
        if (size && !options.includes(size)) {
          options.push(size);
        }
      });

      options.forEach(size => {
        formattedOptions.push({
          text: size,
          value: size,
        });
      });

      this.size = null;

      return formattedOptions;
    },

    sizeImages() {
      if (this.size?.value) {
        return (
          this.availableOptions[this.colour]['sizes'][this.size.value][
            'images'
          ] || null
        );
      }
      return {};
    },

    returnReasonOptions() {
      return this.returnReasons.map(reason => ({
        text: reason.name,
        value: reason.id,
      }));
    },
  },

  methods: {
    getQuantityOptions(item) {
      const options = [];

      for (let index = 1; index <= item.activeQuantity; index++) {
        options.push({
          text: index,
          value: index,
        });
      }

      return options;
    },

    addItem() {
      this.$refs[`setOrderItemReturnNewForm${this.item.id}`]
        .validate()
        .then(() => {
          if (this.forceReason && (!this.reason || !this.reason.value)) {
            this.$dialogs.showSnackBar({
              message: 'Please choose a return reason',
            });
          } else if (this.returnType.value === 'exchange') {
            this.$refs[`setOrderItemReturnForm${this.item.id}`]
              .validateAll()
              .then(success => {
                if (success) {
                  if (!this.colour || !this.size) {
                    this.$dialogs.showSnackBar({
                      message: 'Please choose a size and colour',
                    });
                  } else {
                    this.emitItem();
                    this.added = true;
                  }
                }
              });
          } else {
            this.emitItem();
            this.added = true;
          }
        })
        .catch(() => {
          // noop
        });
    },

    removeItem(item) {
      this.$emit('remove-item', item);
      this.resetFields();
      this.added = false;
    },

    resetFields() {
      this.quantity = {text: 1, value: 1};
      this.returnType = {text: 'Keep', value: 'keep'};
    },

    emitItem() {
      const item = {
        id: this.item.id,
        returnType: this.returnType.value,
        reason: this.reason ? this.reason.value : null,
        optionMatrixRecordId: this.item.optionMatrixRecord.id,
        quantity: this.item.quantity > 1 ? this.quantity.value : 1,
        exchangeFor: {
          options: {
            colour: this.colour,
            size: this.size.value,
          },
          images: this.sizeImages,
        },
      };

      this.$emit('add-item', item);
    },

    getReturnTypeOptions(product) {
      const types = [
        {
          text: 'Keep',
          value: 'keep',
        },
      ];
      //if not kitlocker
      if (this.$store.state.subdomain?.platform?.id !== 56) {
        types.push({
          text: 'Exchange',
          value: 'exchange',
        });
      }

      if (
        this.app.isAtlas ||
        (this.order.hasBeenIn('complete') && !product.isCustomProduct(true))
      ) {
        types.push({
          text: 'Refund',
          value: 'refund',
        });
      }

      return types;
    },
  },
};
</script>

<style lang="scss" scoped>
.product-info {
  background-color: var(--tml-page-background-color);
  border-radius: $border-radius;
  overflow: hidden;
}

.product-preview {
  background-color: #ffffff;
}
</style>
